import axios, { AxiosError } from "axios";
import { auth } from "./firebase";

// Ensure no trailing slash issues
const normalizedApiBaseURL = (import.meta.env.API_BASE_URL ?? "").replace(/\/$/, "");
// Only the local Vite dev server (mode "development") should use a relative base so
// its /api proxy handles requests. Every built deployment (staging, qa, production)
// must use API_BASE_URL. Gating on import.meta.env.DEV was wrong: DEV is derived from
// NODE_ENV, so any build with NODE_ENV !== "production" (e.g. mode-based builds) would
// silently drop the backend URL and fall back to a relative path (Sentry 7586345291).
export const normalizedBaseURL =
  import.meta.env.MODE === "development" ? "" : normalizedApiBaseURL;

export const api = axios.create({
  baseURL: `${normalizedBaseURL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

// Cached at module init so the promise starts resolving during app startup.
// On iOS Safari, calling authStateReady() fresh per-request can stall on cold
// loads and consume the full Axios timeout before the HTTP request even starts.
const authReady = auth.authStateReady();

api.interceptors.request.use(async (config) => {
  // Skip authorization
  const publicEndpoints = [
    "/dev/auth/login",
    "/dev/auth/signup",
    "/send-reset-link",
    "/verify-reset-token",
    "/email-providers",
    "/send-otp",
    "/verify-otp",
    // Establishes a phone session, so by definition there is no session to authorise with
    // (KAN-620). Without this the interceptor waits on authStateReady and attaches whatever
    // token happens to be around -- which for a signed-in user means sending someone else's
    // credentials to a login endpoint.
    "/phone-login",
    "/promotions/preview",
    "/resend-verification-email",
    "/send-magic-link",
    "/office-cleaning/enquiry",
    "/referral/",
    "/settlement/",
  ];

  const isPublic = publicEndpoints.some((url) => config.url?.includes(url));

  if (!isPublic) {
    try {
      await authReady;
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch {
      // Let the backend decide whether the request can proceed without auth.
    }
  }
  return config;
});

// Extend Axios config type to track retry state
declare module "axios" {
  interface InternalAxiosRequestConfig {
    __retried?: boolean;
    __retryCount?: number;
  }
}

// Deduplicates concurrent 401s so only one token refresh fires at a time
let refreshPromise: Promise<string> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (
      error instanceof AxiosError &&
      error.config &&
      error.config.method === "get" &&
      (!error.response || error.response.status >= 500) &&
      (error.config.__retryCount ?? 0) < 2
    ) {
      const retryCount = error.config.__retryCount ?? 0;
      error.config.__retryCount = retryCount + 1;
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
      return api.request(error.config);
    }

    if (
      error instanceof AxiosError &&
      error.response?.status === 401 &&
      error.config &&
      !error.config.__retried
    ) {
      const user = auth.currentUser;
      if (user) {
        if (!refreshPromise) {
          refreshPromise = user.getIdToken(true).finally(() => {
            refreshPromise = null;
          });
        }
        const freshToken = await refreshPromise;
        error.config.__retried = true;
        error.config.headers.Authorization = `Bearer ${freshToken}`;
        return api.request(error.config);
      }
    }
    return Promise.reject(error);
  },
);
